import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { renderHTML } from '@agility/nextjs';
import Link from 'next/link';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItem';
import { getIcon } from '../../services/agilityTranslations';
import ArticleIcon from '@mui/icons-material/Article';
import {
  KitGridContainer,
  KitGridItem,
  KitButton,
} from 'components/common/cms-kit';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexFlow: 'row',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  [theme.breakpoints.up('md')]: {
    padding: '55px 0 10px 0',
  },
  [theme.breakpoints.down('md')]: {
    padding: '35px 0 40px 0',
  },
}));

const ListText = styled('div')({
  fontWeight: 600,
  fontSize: '0.90rem',
  lineHeight: '1.00rem',
});

const _HandoutText = styled('div')({
  fontWeight: 300,
  fontSize: '0.90rem',
  lineHeight: '1.00rem',
});

const DocumentLink = styled('div')({
  justifyContent: 'center',
});

const ActionArea = styled('div')({
  flexGrow: 0,
  padding: '10px 0 10px 2px',
});

const VideoContainer = styled('div')({
  flexGrow: 0,
  flexShrink: 1,
  '& .video': {
    aspectRatio: '16 / 9',
  },
  '@media screen and (max-width: 899px)': {
    display: 'none',
  },
});

const MobileVideoContainer = styled('div')({
  backgroundColor: '#fff',
  flexGrow: 0,
  flexShrink: 1,
  '& .video': {
    aspectRatio: '16 / 9',
    width: '100%',
  },
  '@media screen and (min-width: 900px)': {
    display: 'none',
  },
});

const EmbedContainer = styled('div')(({ $videoSelected }) => ({
  '@media screen and (max-width: 899px)': {
    ...($videoSelected ? { width: '450px', minHeight: '230px' } : { display: 'none' }),
  },
  '@media screen and (min-width: 900px)': {
    margin: '0 0 50px 0',
    ...($videoSelected ? { width: '550px', height: '300px' } : { display: 'none' }),
  },
}));

const ContentContainer = styled('div')({
  flexGrow: 1,
  flexShrink: 0,
  flex: '50%',
  verticalAlign: 'top',
  '@media screen and (max-width: 899px)': {
    paddingRight: '30px',
  },
  '@media screen and (min-width: 900px)': {
    padding: '20px 100px 0 0',
  },
});

const ListContainer = styled('div')(({ $textAlign, $videoSelected }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-start',
  position: 'relative',
  '@media screen and (max-width: 899px)': {
    padding: '0 20px 0 50px',
    width: '95%',
  },
  '@media screen and (min-width: 900px)': {
    padding: '0 40px',
    width: '80%',
    flexDirection: $textAlign === 'left'
      ? $videoSelected
        ? 'row-reverse'
        : 'row'
      : 'row',
  },
}));

const DocumentContainer = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const _ButtonContainer = styled('div')({
  margin: '40px 5px',
  '@media screen and (max-width: 899px)': {
    padding: '0 25px',
  },
});

const Title = styled('div')(({ $textColor }) => ({
  fontWeight: 600,
  margin: '0 5px 10px 5px',
  textTransform: 'uppercase',
  '@media screen and (max-width: 899px)': {
    color: $textColor,
    fontSize: '1.75rem',
    lineHeight: '1.95rem',
  },
  '@media screen and (min-width: 900px)': {
    color: $textColor,
    fontSize: '2.3rem',
    lineHeight: '3rem',
  },
}));

const Description = styled('div')(({ $textColor }) => ({
  margin: '10px 5px',
  '@media screen and (max-width: 899px)': {
    color: $textColor,
    fontSize: '1rem',
    lineHeight: '1.3rem',
  },
  '@media screen and (min-width: 900px)': {
    color: $textColor,
    fontSize: '1.25rem',
    lineHeight: '1.6rem',
  },
}));

const VideoWithIconTitleDescButton = ({ module }) => {
  const { fields } = module;

  const [videoSelected, setVideoSelected] = useState(null);

  const showDocumentLink = (url, iconColor) => {
    return (
      <ListItemIcon>
        <Link href={url.href} target={url.target} rel="noopener noreferrer">
          <ListText style={{ color: iconColor }}>
            <ArticleIcon /> {url.text}
          </ListText>
        </Link>
      </ListItemIcon>
    );
  };

  useEffect(() => {
    if (!videoSelected) {
      if (fields && fields.linkList && fields.linkList.length > 0) {
        setVideoSelected(fields.linkList[0]);
      }
    }
  }, [fields]);

  const [_textAlign, _setTextAlign] = useState('right');

  return (
    <React.Fragment>
      <Container
        style={{
          backgroundImage: `linear-gradient(rgba(0, 58, 93, 0.95),rgba(0, 58, 93, 0.8)), url(${fields.backgroundImage.url})`,
        }}
      >
        <ListContainer $textAlign={fields.textAlign} $videoSelected={videoSelected}>
          <VideoContainer>
            {videoSelected &&
              videoSelected.fields &&
              videoSelected.fields.webinarEmbed && (
                <EmbedContainer
                  $videoSelected={videoSelected}
                  className="prose max-w-full mx-auto"
                  dangerouslySetInnerHTML={renderHTML(
                    videoSelected.fields.webinarEmbed,
                  )}
                />
              )}
            <DocumentContainer>
              <DocumentLink>
                {videoSelected &&
                  videoSelected.fields &&
                  videoSelected.fields.documentUrl &&
                  showDocumentLink(
                    videoSelected.fields.documentUrl,
                    fields.textColor,
                  )}
              </DocumentLink>
            </DocumentContainer>
          </VideoContainer>
          <ContentContainer>
            <ListItemButton
              align="left"
              style={{ width: '100%', padding: 0, margin: 0, marginBottom: 5 }}
            >
              <ListItemIcon style={{ padding: 0, margin: 0 }}>
                {getIcon(fields.iconName, fields.iconColor, '3.00rem')}
              </ListItemIcon>
            </ListItemButton>
            <Title $textColor={fields.textColor}>{fields.title}</Title>
            <Description $textColor={fields.textColor}>{fields.description}</Description>
            <ActionArea>
              <KitGridContainer direction="row" style={{ width: '100%' }}>
                {fields.showActionButton && (
                  <KitGridItem xs={12} align="left">
                    <Link
                      href={fields.actionButtonUrl?.href}
                      rel={`noopener noreferrer${fields.addNoFollowtoActionUrl === 'true' ? ' nofollow' : ''}`}
                    >
                      <KitButton
                        size="sm"
                        round
                        color={fields.actionButtonColor}
                        style={{
                          color: '#fff',
                          fontWeight: 400,
                          padding: '10px 30px',
                          fontSize: '1.20rem',
                        }}
                      >
                        {fields.actionButtonText}
                      </KitButton>
                    </Link>
                  </KitGridItem>
                )}
              </KitGridContainer>
            </ActionArea>
          </ContentContainer>
        </ListContainer>
      </Container>
      <MobileVideoContainer>
        {videoSelected &&
          videoSelected.fields &&
          videoSelected.fields.webinarEmbed && (
            <EmbedContainer
              $videoSelected={videoSelected}
              className="prose max-w-full mx-auto"
              dangerouslySetInnerHTML={renderHTML(
                videoSelected.fields.webinarEmbed,
              )}
            />
          )}
        <DocumentContainer>
          <DocumentLink>
            {videoSelected &&
              videoSelected.fields &&
              videoSelected.fields.documentUrl &&
              showDocumentLink(
                videoSelected.fields.documentUrl,
                fields.textColor,
              )}
          </DocumentLink>
        </DocumentContainer>
      </MobileVideoContainer>
    </React.Fragment>
  );
};

VideoWithIconTitleDescButton.getCustomInitialProps = async function ({
  agility,
  languageCode,
  _channelName,
}) {
  const api = agility;
  let contentItem = null;
  try {
    let list = await api.getContentList({
      referenceName: 'videoWithBullets',
      locale: languageCode,
      expandAllContentLinks: true,
      take: 1,
    });
    if (list && list.items && list.items.length > 0) {
      contentItem = list;
    } else {
      return null;
    }
  } catch (error) {
    if (console)
      console.error('Could not load Video with Bullets video List.', error);
    return null;
  }

  return {
    fields: contentItem,
  };
};
export default VideoWithIconTitleDescButton;
